import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Clock, Car, ChevronLeft, ChevronRight, Check, User, Phone, Mail, MessageSquare, Search } from 'lucide-react';
import { toast } from 'sonner';
import { testDriveApi, carsApi } from '@/lib/api';
import { Navbar, Footer } from '@/components/Layout';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import TurnstileWidget from '@/components/TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function TestDrivePage() {
  const [searchParams] = useSearchParams();
  const preselectedCarId = searchParams.get('car');
  
  const { settings } = useSiteSettings();
  const { language, t } = useLanguage();
  
  const [cars, setCars] = useState([]);
  const [carSearch, setCarSearch] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const WEEKDAYS = language === 'de' ? WEEKDAYS_DE : WEEKDAYS_EN;
  const MONTHS = language === 'de' ? MONTHS_DE : MONTHS_EN;

  // Load cars
  useEffect(() => {
    const loadCars = async () => {
      try {
        const response = await carsApi.getAll({ is_active: true, is_sold: false });
        const carList = response.data?.cars || response.data || [];
        setCars(carList);
        
        // Preselect car if provided
        if (preselectedCarId && carList.length > 0) {
          const car = carList.find(c => c.id === preselectedCarId);
          if (car) setSelectedCar(car);
        }
      } catch (err) {
        console.error('Error loading cars:', err);
      } finally {
        setLoading(false);
      }
    };
    loadCars();
  }, [preselectedCarId]);

  // Load calendar - use auto-generated calendar
  useEffect(() => {
    const loadCalendar = async () => {
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const response = await testDriveApi.getAutoCalendar(year, month);
        const data = response.data || response;
        const calMap = {};
        if (Array.isArray(data)) {
          data.forEach(day => {
            calMap[day.date] = day;
          });
        }
        setCalendarData(calMap);
      } catch (err) {
        console.error('Error loading calendar:', err);
      }
    };
    loadCalendar();
  }, [currentMonth]);

  // Load available slots when date is selected
  useEffect(() => {
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }
    
    const loadSlots = async () => {
      try {
        const response = await testDriveApi.getAvailableSlots(selectedDate, selectedCar?.id);
        const data = response.data || response;
        setAvailableSlots(data.slots || []);
      } catch (err) {
        console.error('Error loading slots:', err);
        setAvailableSlots([]);
      }
    };
    loadSlots();
  }, [selectedDate, selectedCar]);

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({ day: i, date: dateStr });
    }
    return days;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCar || !selectedDate || !selectedSlot) {
      toast.error(language === 'de' ? 'Bitte wählen Sie Fahrzeug, Datum und Uhrzeit' : 'Please select car, date and time');
      return;
    }
    
    // Verify Turnstile if configured
    if (settings?.turnstile_site_key && !turnstileToken) {
      toast.error(language === 'de' ? 'Bitte bestätigen Sie das CAPTCHA' : 'Please complete the CAPTCHA');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Verify Turnstile token with backend if configured
      if (turnstileToken) {
        const verifyResponse = await fetch(`${API_URL}/api/captcha/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken })
        });
        const verifyResult = await verifyResponse.json();
        if (!verifyResult.success) {
          toast.error(language === 'de' ? 'CAPTCHA-Überprüfung fehlgeschlagen' : 'CAPTCHA verification failed');
          setSubmitting(false);
          return;
        }
      }
      
      await testDriveApi.book({
        car_id: selectedCar.id,
        date: selectedDate,
        time_slot: selectedSlot,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || null
      });
      
      setSuccess(true);
      setTurnstileToken(null);
      toast.success(language === 'de' ? 'Anfrage erfolgreich gesendet!' : 'Request sent successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || (language === 'de' ? 'Fehler beim Senden' : 'Error submitting request');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  const days = getDaysInMonth(currentMonth);
  const today = new Date().toISOString().split('T')[0];

  const getCarImage = (car) => {
    if (!car?.images?.length) return null;
    const img = car.images[0];
    return img.startsWith('http') ? img : `${API_URL}${img}`;
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-500" size={40} />
            </div>
            <h1 className="text-2xl font-heading font-bold mb-4">
              {language === 'de' ? 'Anfrage gesendet!' : 'Request Sent!'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {language === 'de' 
                ? 'Vielen Dank für Ihre Probefahrt-Anfrage. Wir werden uns schnellstmöglich bei Ihnen melden.'
                : 'Thank you for your test drive request. We will contact you as soon as possible.'}
            </p>
            <Button onClick={() => window.location.href = '/cars'}>
              {language === 'de' ? 'Weitere Fahrzeuge ansehen' : 'View More Vehicles'}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-8 md:pt-28 md:pb-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              {language === 'de' ? settings?.test_drive_title || 'Probefahrt buchen' : settings?.test_drive_title_en || 'Book a Test Drive'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'de' ? settings?.test_drive_subtitle || 'Erleben Sie Ihr Traumauto hautnah' : settings?.test_drive_subtitle_en || 'Experience your dream car up close'}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Step 1: Select Car */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">1</span>
                  {language === 'de' ? 'Fahrzeug wählen' : 'Select Vehicle'}
                </h2>
                
                {/* Search input */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <input
                    type="text"
                    value={carSearch}
                    onChange={(e) => setCarSearch(e.target.value)}
                    placeholder={language === 'de' ? 'Fahrzeug suchen...' : 'Search vehicle...'}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:border-primary focus:outline-none"
                    data-testid="car-search-input"
                  />
                </div>
                
                {loading ? (
                  <div className="animate-pulse space-y-2">
                    {[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-lg"></div>)}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {cars
                      .filter(car => {
                        if (!carSearch) return true;
                        const search = carSearch.toLowerCase();
                        return (
                          car.brand?.toLowerCase().includes(search) ||
                          car.model?.toLowerCase().includes(search) ||
                          `${car.brand} ${car.model}`.toLowerCase().includes(search)
                        );
                      })
                      .map(car => (
                      <button
                        key={car.id}
                        type="button"
                        onClick={() => setSelectedCar(car)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-all text-left ${
                          selectedCar?.id === car.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="w-16 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                          {getCarImage(car) && (
                            <img src={getCarImage(car)} alt={car.model} className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{car.brand} {car.model}</p>
                          <p className="text-xs text-muted-foreground">{car.year} • {car.horsepower} PS</p>
                        </div>
                        {selectedCar?.id === car.id && (
                          <Check className="text-primary flex-shrink-0" size={18} />
                        )}
                      </button>
                    ))}
                    {cars.filter(car => {
                      if (!carSearch) return true;
                      const search = carSearch.toLowerCase();
                      return (
                        car.brand?.toLowerCase().includes(search) ||
                        car.model?.toLowerCase().includes(search) ||
                        `${car.brand} ${car.model}`.toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {language === 'de' ? 'Keine Fahrzeuge gefunden' : 'No vehicles found'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: Select Date & Time */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">2</span>
                  {language === 'de' ? 'Datum & Uhrzeit' : 'Date & Time'}
                </h2>
                
                {/* Mini Calendar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <button type="button" onClick={prevMonth} className="p-1 hover:bg-muted rounded">
                      <ChevronLeft size={18} />
                    </button>
                    <span className="font-medium text-sm">
                      {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                    <button type="button" onClick={nextMonth} className="p-1 hover:bg-muted rounded">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-0.5 text-xs">
                    {WEEKDAYS.map(day => (
                      <div key={day} className="text-center text-muted-foreground py-1">{day}</div>
                    ))}
                    {days.map((dayInfo, idx) => {
                      if (!dayInfo) return <div key={`e-${idx}`}></div>;
                      
                      const dayData = calendarData[dayInfo.date];
                      const isAvailable = dayData?.available;
                      const isPast = dayInfo.date < today;
                      const isSelected = dayInfo.date === selectedDate;
                      
                      return (
                        <button
                          key={dayInfo.date}
                          type="button"
                          onClick={() => isAvailable && !isPast && setSelectedDate(dayInfo.date)}
                          disabled={!isAvailable || isPast}
                          className={`
                            aspect-square rounded text-xs transition-all
                            ${isPast ? 'opacity-30 cursor-not-allowed' : ''}
                            ${isAvailable && !isPast ? 'cursor-pointer hover:bg-primary/10' : 'cursor-not-allowed'}
                            ${isAvailable ? 'text-green-600 font-medium' : 'text-muted-foreground'}
                            ${isSelected ? 'bg-primary text-primary-foreground' : ''}
                          `}
                        >
                          {dayInfo.day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div>
                    <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
                      <Clock size={14} />
                      {language === 'de' ? 'Verfügbare Zeiten' : 'Available Times'}
                    </h3>
                    {availableSlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {language === 'de' ? 'Keine Zeiten verfügbar' : 'No times available'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availableSlots.map(slot => (
                          <button
                            key={slot.slot}
                            type="button"
                            onClick={() => setSelectedSlot(slot.slot)}
                            className={`p-2 text-sm rounded-lg border transition-all ${
                              selectedSlot === slot.slot
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            {slot.start} - {slot.end}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 3: Contact Info */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">3</span>
                  {language === 'de' ? 'Ihre Daten' : 'Your Information'}
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">
                      <User size={14} className="inline mr-1" />
                      {language === 'de' ? 'Name' : 'Name'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-transparent border-b border-border px-0 py-3 text-sm text-foreground placeholder:text-muted-foreground placeholder:italic focus:outline-none focus:border-primary transition-colors"
                      placeholder={language === 'de' ? 'Ihr vollständiger Name' : 'Your full name'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">
                      <Mail size={14} className="inline mr-1" />
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full bg-transparent border-b border-border px-0 py-3 text-sm text-foreground placeholder:text-muted-foreground placeholder:italic focus:outline-none focus:border-primary transition-colors"
                      placeholder="email@beispiel.de"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">
                      <Phone size={14} className="inline mr-1" />
                      {language === 'de' ? 'Telefon' : 'Phone'} *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full bg-transparent border-b border-border px-0 py-3 text-sm text-foreground placeholder:text-muted-foreground placeholder:italic focus:outline-none focus:border-primary transition-colors"
                      placeholder="+43 123 456 789"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">
                      <MessageSquare size={14} className="inline mr-1" />
                      {language === 'de' ? 'Nachricht (optional)' : 'Message (optional)'}
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full bg-transparent border-b border-border px-0 py-3 text-sm text-foreground placeholder:text-muted-foreground placeholder:italic focus:outline-none focus:border-primary transition-colors min-h-[100px] resize-none"
                      placeholder={language === 'de' ? 'Besondere Wünsche oder Fragen...' : 'Special requests or questions...'}
                    />
                  </div>
                </div>

                {/* Summary & Submit */}
                <div className="mt-4 pt-4 border-t border-border">
                  {selectedCar && selectedDate && selectedSlot && (
                    <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm">
                      <p className="font-medium mb-1">{language === 'de' ? 'Zusammenfassung:' : 'Summary:'}</p>
                      <p>🚗 {selectedCar.brand} {selectedCar.model}</p>
                      <p>📅 {selectedDate}</p>
                      <p>🕐 {selectedSlot}</p>
                    </div>
                  )}
                  
                  {/* Turnstile CAPTCHA */}
                  <div className="mb-4">
                    <TurnstileWidget 
                      onVerify={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={submitting || !selectedCar || !selectedDate || !selectedSlot}
                    className="w-full"
                  >
                    {submitting 
                      ? (language === 'de' ? 'Wird gesendet...' : 'Sending...') 
                      : (language === 'de' ? 'Probefahrt anfragen' : 'Request Test Drive')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
